import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular-ivy';
import { Breadcrumb, Exception } from '@sentry/types';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  private moduleMap: Record<string, string> = {
    '/public/make-payment': 'payment',
    '/public/auth': 'authentication',
    '/wallet': 'wallet',
    '/transactions': 'transactions',
    '/student-payments': 'student-payments',
    '/payouts': 'payouts',
    '/bank-accounts': 'bank-accounts',
    '/settings': 'settings',
    // Add more mappings as needed
  };

  constructor(private router: Router) {
    // Track route changes as breadcrumbs
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        this.addBreadcrumb({
          type: 'navigation',
          category: 'navigation',
          data: {
            from: this.router
              .getCurrentNavigation()
              ?.previousNavigation?.finalUrl?.toString(),
            to: event.url,
          },
          level: 'info',
        });

        // Set current module tag based on URL
        this.setCurrentModule(event.url);
      });
  }

  /**
   * Capture an exception with enhanced context
   */
  captureException(error: Exception | HttpErrorResponse, context?: any): void {
    Sentry.withScope((scope) => {
      // Add error context
      if (context) {
        Object.keys(context).forEach((key) => {
          scope.setExtra(key, context[key]);
        });
      }

      // Process HttpErrorResponse to extract detailed information
      if (this.isHttpErrorResponse(error)) {
        const httpError = error as any; // Type as any to access properties

        // Extract error details as a record to avoid TypeScript errors
        const errorDetails: Record<string, any> = {
          status: httpError.status,
          statusText: httpError.statusText,
          url: httpError.url,
          name: httpError.name,
          message: httpError.message,
        };

        // Extract error response body if available
        if (httpError.error) {
          if (typeof httpError.error === 'string') {
            errorDetails['responseBody'] = httpError.error;
          } else if (typeof httpError.error === 'object') {
            // Extract all properties from the error object
            Object.keys(httpError.error).forEach((key) => {
              // Handle nested error objects
              if (key === 'errors' && Array.isArray(httpError.error.errors)) {
                // Format array of errors
                errorDetails['response_errors'] = JSON.stringify(
                  httpError.error.errors,
                );

                // Add individual errors for better visibility
                httpError.error.errors.forEach((err: any, index: number) => {
                  if (typeof err === 'string') {
                    errorDetails[`response_error_${index}`] = err;
                  } else if (typeof err === 'object') {
                    Object.keys(err).forEach((errKey) => {
                      errorDetails[`response_error_${index}_${errKey}`] =
                        err[errKey];
                    });
                  }
                });
              } else if (
                key === 'error' &&
                typeof httpError.error.error === 'object'
              ) {
                // Handle nested error object
                Object.keys(httpError.error.error).forEach((errKey) => {
                  errorDetails[`response_error_${errKey}`] =
                    httpError.error.error[errKey];
                });
              } else {
                errorDetails[`response_${key}`] = httpError.error[key];
              }
            });
          }
        }

        // Add all extracted details as extra context
        Object.entries(errorDetails).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });

        // Create a more informative error object to send to Sentry
        let errorMessage = `HTTP Error ${httpError.status}: ${
          httpError.statusText || 'Unknown'
        }`;

        // Add more detailed error information if available
        if (httpError.error) {
          if (typeof httpError.error === 'string') {
            errorMessage += ` - ${httpError.error}`;
          } else if (typeof httpError.error === 'object') {
            // Add message from error object if available
            if (httpError.error.message) {
              errorMessage += ` - ${httpError.error.message}`;
            }

            // Add error description if available
            if (httpError.error.error_description) {
              errorMessage += ` (${httpError.error.error_description})`;
            }
          }
        } else {
          // Fallback to default message
          errorMessage += ` - ${httpError.message}`;
        }

        const enhancedError = new Error(errorMessage);

        // Copy the stack trace if available
        if (httpError.stack) {
          enhancedError.stack = httpError.stack;
        }

        // Set error category and severity
        const category = this.getErrorCategory(error);
        scope.setTag('error_category', category);
        scope.setLevel(this.determineErrorSeverity(error));

        // Set fingerprint for better grouping
        scope.setFingerprint(this.generateFingerprint(error, category));

        Sentry.captureException(enhancedError);
      } else {
        // Handle non-HTTP errors normally
        // Set error category and severity
        const category = this.getErrorCategory(error);
        scope.setTag('error_category', category);
        scope.setLevel(this.determineErrorSeverity(error));

        // Set fingerprint for better grouping
        scope.setFingerprint(this.generateFingerprint(error, category));

        Sentry.captureException(error);
      }
    });
  }

  /**
   * Capture a message with specified severity
   */
  captureMessage(
    message: string,
    level: Sentry.Severity | string = 'info',
    context?: any,
  ): void {
    Sentry.withScope((scope) => {
      if (context) {
        Object.keys(context).forEach((key) => {
          scope.setExtra(key, context[key]);
        });
      }

      scope.setLevel(level as Sentry.Severity);
      Sentry.captureMessage(message);
    });
  }

  /**
   * Add a breadcrumb to the current scope
   */
  addBreadcrumb(breadcrumb: Breadcrumb): void {
    Sentry.addBreadcrumb(breadcrumb);
  }

  /**
   * Set a tag on the current scope
   */
  setTag(key: string, value: string): void {
    Sentry.setTag(key, value);
  }

  /**
   * Set user information
   */
  setUser(user: {
    id?: string;
    email?: string;
    username?: string;
    [key: string]: any;
  }): void {
    Sentry.setUser(user);
  }

  /**
   * Configure the current scope
   */
  configureScope(callback: (scope: Sentry.Scope) => void): void {
    Sentry.configureScope(callback);
  }

  /**
   * Start a performance transaction
   */
  startTransaction(context: {
    name: string;
    op: string;
    [key: string]: any;
  }): any {
    return Sentry.startInactiveSpan(context);
  }

  /**
   * Determine the severity of an error
   */
  private determineErrorSeverity(error: any): Sentry.Severity {
    // For HTTP errors
    if (error.status) {
      if (error.status >= 500) return 'fatal' as Sentry.Severity;
      if (error.status === 401 || error.status === 403)
        return 'info' as Sentry.Severity;
      if (error.status === 404) return 'warning' as Sentry.Severity;
      if (error.status >= 400) return 'warning' as Sentry.Severity;
      return 'error' as Sentry.Severity;
    }

    // For client-side errors
    if (error instanceof ReferenceError || error instanceof SyntaxError) {
      return 'fatal' as Sentry.Severity;
    }
    if (error instanceof TypeError) {
      return 'info' as Sentry.Severity;
    }

    // Default severity
    return 'error' as Sentry.Severity;
  }

  /**
   * Categorize the error by type and source
   */
  private getErrorCategory(error: any): string {
    // HTTP errors
    if (error.status) {
      if (error.url) {
        // Extract API endpoint for categorization
        const urlPath = new URL(error.url).pathname;
        if (urlPath.includes('/api/v1/payments')) return 'api:payments';
        if (urlPath.includes('/api/v1/auth')) return 'api:authentication';
        if (urlPath.includes('/api/v1/wallet')) return 'api:wallet';
        if (urlPath.includes('/api/v1/transactions')) return 'api:transactions';
        if (urlPath.includes('/api/v1/student-payments'))
          return 'api:student-payments';
        if (urlPath.includes('/api/v1/payouts')) return 'api:payouts';
        if (urlPath.includes('/api/v1/bank-accounts'))
          return 'api:bank-accounts';
        // Add more API path categorizations
        return 'api:other';
      }
      return 'api:unknown';
    }

    // Client-side errors
    if (error.ngDebugContext) {
      const componentName = error.ngDebugContext.component?.constructor?.name;
      if (componentName) {
        if (componentName.includes('Payment')) return 'client:payments';
        if (componentName.includes('Auth')) return 'client:authentication';
        if (componentName.includes('Wallet')) return 'client:wallet';
        if (componentName.includes('Transaction')) return 'client:transactions';
        if (componentName.includes('Student')) return 'client:student-payments';
        if (componentName.includes('Payout')) return 'client:payouts';
        if (componentName.includes('BankAccount'))
          return 'client:bank-accounts';
        // Add more component categorizations
      }
    }

    // Error type-based categorization
    if (error instanceof ReferenceError) return 'client:reference-error';
    if (error instanceof TypeError) return 'client:type-error';
    if (error instanceof SyntaxError) return 'client:syntax-error';

    return 'client:unknown';
  }

  /**
   * Generate a fingerprint for better error grouping
   */
  private generateFingerprint(error: any, category: string): string[] {
    const fingerprint = ['{{ default }}', category];

    // For HTTP errors, include status code but not specific IDs or tokens
    if (error.status) {
      fingerprint.push(`status-${error.status}`);

      // Extract API endpoint path without IDs
      if (error.url) {
        const urlPath = new URL(error.url).pathname;
        // Replace IDs in URLs like /api/users/123 with /api/users/:id
        const normalizedPath = urlPath.replace(/\/\d+(\/?)/g, '/:id$1');
        fingerprint.push(normalizedPath);
      }
    } else {
      // For client errors, use error name and message without variable parts
      fingerprint.push(error.name || 'Error');

      // Normalize error message by removing variable parts
      if (error.message) {
        // Replace specific values like IDs, timestamps, etc. with placeholders
        const normalizedMessage = error.message
          .replace(/\d+/g, ':num')
          .replace(/(['"]).*?\1/g, ':string');
        fingerprint.push(normalizedMessage);
      }
    }

    return fingerprint;
  }

  /**
   * Check if an error is an HttpErrorResponse
   */
  private isHttpErrorResponse(error: any): boolean {
    return (
      error &&
      typeof error === 'object' &&
      'status' in error &&
      'statusText' in error &&
      'url' in error
    );
  }

  /**
   * Set the current module based on URL
   */
  private setCurrentModule(url: string): void {
    // Find the matching module for the current URL
    const [_ngIgnore, ngModuleName] = Object.entries(this.moduleMap).find(
      ([path]) => url.startsWith(path),
    ) ?? ['module', 'other'];

    this.setTag('module', ngModuleName);
  }
}
